"use client";

import { ChartLine as LineChart, Cpu, Users } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  icon: LineChart,
  name: "Unbiased Truth",
  description: "We prioritize factual accuracy and multi-perspective reporting, ensuring our coverage remains independent, transparent, and free from corporate or political influence."
}, {
  icon: Cpu,
  name: "Digital Accessibility",
  description: "Our platform is built for everyone. We leverage modern technology to ensure our multimedia content is inclusive, screen-reader friendly, and available across all devices."
}, {
  icon: Users,
  name: "Community Impact",
  description: "We amplify underrepresented voices by building a space where grassroots stories are heard, fostering dialogue that strengthens our collective civic engagement."
}];
export default function Values() {
  const header = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="10738" id="our-values" className="dark bg-background border-t border-border py-32 md:py-44">
      <div className="mx-auto w-full max-w-[1440px] px-5 md:px-6 lg:px-12">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 md:grid-cols-12 gap-6 transition-all duration-[420ms] ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="md:col-span-12">
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.2vw, 4rem)",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1.02
          }}>
              {"Our core "}
              <span className="relative inline-block">
                principles
                <span className="absolute left-0 right-0 bg-[var(--ring)]" style={{
                height: "2px",
                bottom: "0.08em"
              }} aria-hidden="true" />
              </span>
              {"."}
            </h2>
          </div>
          <div className="md:col-span-6">
            <p className="font-default text-muted-foreground mt-6" style={{
            fontSize: "clamp(1.0625rem, 1.1vw, 1.1875rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>
              Inclusive Voices News is guided by a commitment to integrity and inclusion. Every story we share is vetted for accuracy and designed to empower our diverse community.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-3 border-t border-border">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCell key={pillar.name} index={i} Icon={Icon} name={pillar.name} description={pillar.description} isLast={i === pillars.length - 1} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCell({
  index,
  Icon,
  name,
  description,
  isLast
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  name: string;
  description: string;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <div ref={ref} data-index={index} className={`group relative px-0 md:px-8 py-12 md:py-12 border-b md:border-b-0 border-border ${!isLast ? "md:border-r" : ""} transition-all duration-[420ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
    transitionDelay: `${index * 60}ms`
  }}>
      {}
      <span className="hidden md:block absolute left-0 top-0 bottom-0 w-px bg-border group-hover:w-0.5 group-hover:bg-[var(--ring)] transition-all duration-[220ms] ease-out" aria-hidden="true" />

      <div className="md:pl-8">
        <div className="transition-transform duration-[220ms] ease-out group-hover:translate-y-1">
          <Icon className="w-6 h-6 text-foreground" strokeWidth={1.25} />
        </div>

        <div style={{
        height: "64px"
      }} />

        <h3 className="font-default text-foreground" style={{
        fontSize: "clamp(1.5rem, 2vw, 2rem)",
        fontWeight: 600,
        letterSpacing: "-0.02em",
        lineHeight: 1.12
      }}>
          {name}
        </h3>

        <div style={{
        height: "16px"
      }} />

        <p className="font-default text-muted-foreground" style={{
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.6
      }}>
          {description}
        </p>

        <div style={{
        height: "48px"
      }} />

        <Link to="/NewsroomPage" className="inline-flex items-center font-default text-foreground" style={{
        fontSize: "0.8125rem",
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        lineHeight: 1.3
      }}>
          <span className="relative">
            Explore news
            <span className="absolute left-0 right-0 bg-[var(--ring)] transition-all duration-[180ms] ease-out group-hover:h-0.5" style={{
            height: "1px",
            bottom: "-2px"
          }} aria-hidden="true" />
          </span>
          <span className="ml-2" aria-hidden="true">→</span>
        </Link>
      </div>
    </div>;
}